import { useOptionalPrivanaI18n } from '@/i18n/PrivanaI18nProvider'
import React, { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useMemberPortalApi } from '../api/memberPortalApiClient'
import BackPillButton from '../components/BackPillButton'
import GolfCourseSplitLayout from '../components/GolfCourseSplitLayout'
import GolfDateCarousel from '../components/GolfDateCarousel'
import {
  DEFAULT_GOLF_TIME_ZONE,
  formatLongDate,
  formatMonthShort,
  formatWeekdayShort,
  getTodayDateKey,
  normalizeDateKey,
  parseDateKey,
} from '../utils/golfTime'

function formatDayLabel(dateKey, timeZone, locale) {
  const weekday = formatWeekdayShort(dateKey, timeZone, locale)
  const parsed = parseDateKey(dateKey)
  const dayNum = parsed ? parsed.getUTCDate() : ''
  return `${weekday || ''} ${dayNum}`.trim()
}

function formatDuration(durationMinutes, prepTime, locale) {
  const parts = []
  if (Number.isFinite(durationMinutes)) {
    parts.push(new Intl.NumberFormat(locale, { style: 'unit', unit: 'minute', unitDisplay: 'short' }).format(durationMinutes))
  }
  return parts.join(' • ')
}

export default function MemberSpaServiceAvailabilityPage({ clubCode }) {
  const { t: memberText, locale } = useOptionalPrivanaI18n()

  const { serviceId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { memberPortalApiGet } = useMemberPortalApi()
  const { t } = useOptionalPrivanaI18n()
  const [selectedDateKey, setSelectedDateKey] = useState(() => normalizeDateKey(searchParams.get('date')))
  const [service, setService] = useState(location.state?.service || null)
  const [serviceLoading, setServiceLoading] = useState(!location.state?.service)
  const [serviceError, setServiceError] = useState(null)
  const [availability, setAvailability] = useState([])
  const [availabilityLoading, setAvailabilityLoading] = useState(false)
  const [availabilityError, setAvailabilityError] = useState(null)
  const [durationMinutes, setDurationMinutes] = useState(null)
  const [prepTime, setPrepTime] = useState(null)
  const [timeZone, setTimeZone] = useState(DEFAULT_GOLF_TIME_ZONE)

  const serviceName = service?.name || 'Spa service'

  useEffect(() => {
    let cancelled = false

    async function loadService() {
      if (!clubCode || !serviceId) return
      setServiceLoading(true)
      setServiceError(null)
      try {
        const res = await memberPortalApiGet(`/api/members-portal/${clubCode}/spa/services/${serviceId}`)
        const json = await res.json().catch(() => null)
        if (!res.ok) {
          throw new Error(json?.error || 'unable_to_load_service')
        }
        if (!cancelled) {
          setService(json?.service || null)
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load spa service', err)
          setServiceError('facilityBooking.serviceFailed')
        }
      } finally {
        if (!cancelled) {
          setServiceLoading(false)
        }
      }
    }

    loadService()

    return () => {
      cancelled = true
    }
  }, [clubCode, memberPortalApiGet, serviceId])

  useEffect(() => {
    if (selectedDateKey) return
    const todayKey = getTodayDateKey(DEFAULT_GOLF_TIME_ZONE)
    if (todayKey) {
      setSelectedDateKey(todayKey)
    }
  }, [selectedDateKey])

  useEffect(() => {
    if (!selectedDateKey) return
    const existingDate = searchParams.get('date')
    if (existingDate === selectedDateKey) return
    const next = new URLSearchParams(searchParams)
    next.set('date', selectedDateKey)
    navigate({ search: `?${next.toString()}` }, { replace: true })
  }, [navigate, searchParams, selectedDateKey])

  useEffect(() => {
    let cancelled = false

    async function loadAvailability() {
      if (!clubCode || !serviceId || !selectedDateKey) return
      setAvailabilityLoading(true)
      setAvailabilityError(null)
      try {
        const res = await memberPortalApiGet(
          `/api/members-portal/${clubCode}/spa/services/${serviceId}/availability?date=${selectedDateKey}`
        )
        const json = await res.json().catch(() => null)
        if (!res.ok) {
          throw new Error(json?.error || 'availability_unavailable')
        }
        if (!cancelled) {
          setAvailability(Array.isArray(json?.slots) ? json.slots : [])
          setDurationMinutes(json?.duration_minutes ?? null)
          setPrepTime(json?.prep_time ?? null)
          setTimeZone(json?.time_zone || DEFAULT_GOLF_TIME_ZONE)
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load spa availability', err)
          setAvailabilityError('facilityBooking.previewFailed')
          setAvailability([])
        }
      } finally {
        if (!cancelled) {
          setAvailabilityLoading(false)
        }
      }
    }

    loadAvailability()

    return () => {
      cancelled = true
    }
  }, [clubCode, memberPortalApiGet, selectedDateKey, serviceId])

  const handleBook = (slot) => {
    if (!slot?.start_time || !slot?.start_at || !selectedDateKey) return
    const next = new URLSearchParams()
    next.set('date', selectedDateKey)
    next.set('start_time', slot.start_time)
    next.set('start_at', slot.start_at)
    if (durationMinutes != null) next.set('duration', String(durationMinutes))
    if (prepTime != null) next.set('prep', String(prepTime))
    if (slot.room_id) next.set('room_id', slot.room_id)
    navigate(`/${clubCode}/spa/${serviceId}/confirm?${next.toString()}`, {
      state: { service, slot, durationMinutes, prepTime },
    })
  }

  const formattedDate = useMemo(
    () => (selectedDateKey ? formatLongDate(selectedDateKey, timeZone, locale) : null),
    [selectedDateKey, timeZone, locale]
  )

  const slotMeta = useMemo(() => formatDuration(durationMinutes, prepTime, locale), [durationMinutes, prepTime, locale])

  return (
    <GolfCourseSplitLayout heroImageUrl={service?.image_url || '/images/luxury_club_image.png'} heroPosition="50% 45%">
      <div className="mp-golf-header">
        <div className="mp-golf-header__details">
          <p className="mp-eyebrow">{memberText("portalPresentation.member_available_times")}</p>
          <h4 className="mp-panel__title">{serviceName}</h4>
          {formattedDate ? <p className="mp-text-muted">{formattedDate}</p> : null}
        </div>
        <div className="mp-golf-header__actions">
          <BackPillButton to={`/${clubCode}/spa`}>{memberText("portalPresentation.member_back_to_services")}</BackPillButton>
        </div>
      </div>

      <div className="mp-golf-scroll-area">
        <GolfDateCarousel
          bookingWindow={14}
          timeZone={timeZone}
          selectedDateKey={selectedDateKey}
          onSelectDate={setSelectedDateKey}
          formatPrimary={(dateKey) => formatDayLabel(dateKey, timeZone, locale)}
          formatSecondary={(dateKey) => formatMonthShort(dateKey, timeZone, locale)}
        />

        <div className="mp-tee-slot-scroll">
          {serviceError ? <div className="mp-warning">{memberText(serviceError)}</div> : null}
          {availabilityError ? <div className="mp-warning">{memberText(availabilityError)}</div> : null}

          {serviceLoading || availabilityLoading ? (
            <p className="mp-text-muted">{memberText("portalPresentation.member_loading_availability")}</p>
          ) : availability.length === 0 ? (
            <div className="mp-empty">{memberText("portalPresentation.member_no_available_times_for_this_day")}</div>
          ) : (
            <div className="mp-tee-slot-list">
              {availability.map((slot) => (
                <div key={slot.start_at || slot.start_time} className="mp-tee-slot">
                  <div>
                    <div className="mp-tee-slot__time">{slot.start_time}</div>
                    {slotMeta ? <div className="mp-tee-slot__meta">{slotMeta}</div> : null}
                  </div>
                  <div className="mp-tee-slot__actions">
                    <button
                      type="button"
                      className="mp-btn mp-btn--primary"
                      onClick={() => handleBook(slot)}
                    > {memberText("portalPresentation.member_book_now")} </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </GolfCourseSplitLayout>
  )
}
